const BASE = "/api/grid";

function getAuthHeader(): string {
  const creds = sessionStorage.getItem("grid-credentials");
  if (!creds) return "";
  const { apiTokenId, apiClientSecret } = JSON.parse(creds);
  return "Basic " + btoa(`${apiTokenId}:${apiClientSecret}`);
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: getAuthHeader(),
    ...(options.headers as Record<string, string>),
  };

  const url = `${BASE}${path}`;
  console.log(`[Grid API] ${options.method || "GET"} ${url}`);

  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    console.error(`[Grid API] ${res.status} ${path}`, body);
    throw new ApiError(res.status, body.message || body.error || res.statusText, body);
  }

  if (res.status === 204) return {} as T;
  const data = await res.json();
  console.log(`[Grid API] ${res.status} ${path}`, Array.isArray(data?.data) ? `(${data.data.length} items)` : "");
  return data;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Customers ──────────────────────────────────────────────

export interface ListResponse<T> {
  data: T[];
  hasMore: boolean;
  nextCursor?: string;
  totalCount?: number;
}

export async function listCustomers(params?: {
  limit?: number;
  cursor?: string;
  customerType?: string;
}): Promise<ListResponse<any>> {
  const q = new URLSearchParams();
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.cursor) q.set("cursor", params.cursor);
  if (params?.customerType) q.set("customerType", params.customerType);
  const qs = q.toString();
  return request(`/customers${qs ? `?${qs}` : ""}`);
}

export async function getCustomer(customerId: string): Promise<any> {
  return request(`/customers/${encodeURIComponent(customerId)}`);
}

export async function createCustomer(body: Record<string, unknown>): Promise<any> {
  return request("/customers", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateCustomer(
  customerId: string,
  body: Record<string, unknown>
): Promise<any> {
  return request(`/customers/${encodeURIComponent(customerId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteCustomer(customerId: string): Promise<any> {
  return request(`/customers/${encodeURIComponent(customerId)}`, {
    method: "DELETE",
  });
}

// ─── Internal Accounts ──────────────────────────────────────

export async function listCustomerInternalAccounts(params?: {
  customerId?: string;
  currency?: string;
  limit?: number;
  cursor?: string;
}): Promise<ListResponse<any>> {
  const q = new URLSearchParams();
  if (params?.customerId) q.set("customerId", params.customerId);
  if (params?.currency) q.set("currency", params.currency);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.cursor) q.set("cursor", params.cursor);
  const qs = q.toString();
  return request(`/customers/internal-accounts${qs ? `?${qs}` : ""}`);
}

export async function listPlatformInternalAccounts(params?: {
  currency?: string;
}): Promise<{ data: any[] }> {
  const q = new URLSearchParams();
  if (params?.currency) q.set("currency", params.currency);
  const qs = q.toString();
  return request(`/platform/internal-accounts${qs ? `?${qs}` : ""}`);
}

// ─── External Accounts ──────────────────────────────────────

export async function listCustomerExternalAccounts(params?: {
  customerId?: string;
  currency?: string;
  limit?: number;
  cursor?: string;
}): Promise<ListResponse<any>> {
  const q = new URLSearchParams();
  if (params?.customerId) q.set("customerId", params.customerId);
  if (params?.currency) q.set("currency", params.currency);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.cursor) q.set("cursor", params.cursor);
  const qs = q.toString();
  return request(`/customers/external-accounts${qs ? `?${qs}` : ""}`);
}

export async function createExternalAccount(body: {
  customerId: string;
  currency: string;
  platformAccountId?: string;
  defaultUmaDepositAccount?: boolean;
  accountInfo: Record<string, unknown>;
}): Promise<any> {
  return request("/customers/external-accounts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── Same-Currency Transfers ────────────────────────────────

export async function transferIn(body: {
  source: { accountId: string };
  destination: { accountId: string };
  amount?: number;
}): Promise<any> {
  return request("/transfer-in", {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Idempotency-Key": crypto.randomUUID() },
  });
}

export async function transferOut(body: {
  source: { accountId: string };
  destination: { accountId: string };
  amount?: number;
}): Promise<any> {
  return request("/transfer-out", {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Idempotency-Key": crypto.randomUUID() },
  });
}

// ─── Cross-Currency Transfers (Quotes) ──────────────────────

export async function createQuote(body: {
  source: { sourceType?: string; accountId?: string; customerId?: string; currency?: string };
  destination: {
    destinationType?: string;
    accountId?: string;
    umaAddress?: string;
    currency?: string;
    externalAccountDetails?: Record<string, unknown>;
  };
  lockedCurrencySide: "SENDING" | "RECEIVING";
  lockedCurrencyAmount: number;
  immediatelyExecute?: boolean;
  description?: string;
  lookupId?: string;
  senderCustomerInfo?: Record<string, unknown>;
}): Promise<any> {
  return request("/quotes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listQuotes(params?: {
  customerId?: string;
  status?: string;
  limit?: number;
  cursor?: string;
}): Promise<ListResponse<any>> {
  const q = new URLSearchParams();
  if (params?.customerId) q.set("customerId", params.customerId);
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.cursor) q.set("cursor", params.cursor);
  const qs = q.toString();
  return request(`/quotes${qs ? `?${qs}` : ""}`);
}

export async function getQuote(quoteId: string): Promise<any> {
  return request(`/quotes/${encodeURIComponent(quoteId)}`);
}

export async function executeQuote(quoteId: string): Promise<any> {
  return request(`/quotes/${encodeURIComponent(quoteId)}/execute`, {
    method: "POST",
  });
}

// ─── Receiver Lookup ────────────────────────────────────────

export async function lookupUma(
  receiverUmaAddress: string,
  params?: { senderUmaAddress?: string; customerId?: string }
): Promise<any> {
  const q = new URLSearchParams();
  if (params?.senderUmaAddress) q.set("senderUmaAddress", params.senderUmaAddress);
  if (params?.customerId) q.set("customerId", params.customerId);
  const qs = q.toString();
  return request(
    `/receiver/uma/${encodeURIComponent(receiverUmaAddress)}${qs ? `?${qs}` : ""}`
  );
}

// ─── Transactions ───────────────────────────────────────────

export async function listTransactions(params?: {
  customerId?: string;
  status?: string;
  type?: string;
  limit?: number;
  cursor?: string;
  sortOrder?: "asc" | "desc";
}): Promise<ListResponse<any>> {
  const q = new URLSearchParams();
  if (params?.customerId) q.set("customerId", params.customerId);
  if (params?.status) q.set("status", params.status);
  if (params?.type) q.set("type", params.type);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.cursor) q.set("cursor", params.cursor);
  if (params?.sortOrder) q.set("sortOrder", params.sortOrder);
  const qs = q.toString();
  return request(`/transactions${qs ? `?${qs}` : ""}`);
}

export async function getTransaction(transactionId: string): Promise<any> {
  return request(`/transactions/${encodeURIComponent(transactionId)}`);
}

export async function approveTransaction(transactionId: string): Promise<any> {
  return request(
    `/transactions/${encodeURIComponent(transactionId)}/approve`,
    { method: "POST" }
  );
}

export async function rejectTransaction(
  transactionId: string,
  reason?: string
): Promise<any> {
  return request(
    `/transactions/${encodeURIComponent(transactionId)}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    }
  );
}

// ─── Invitations ────────────────────────────────────────────

export async function createInvitation(body: {
  inviterUma: string;
  firstName?: string;
  amountToSend?: number;
  expiresAt?: string;
}): Promise<any> {
  return request("/invitations", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getInvitation(invitationCode: string): Promise<any> {
  return request(`/invitations/${encodeURIComponent(invitationCode)}`);
}

export async function claimInvitation(
  invitationCode: string,
  inviteeUma: string
): Promise<any> {
  return request(
    `/invitations/${encodeURIComponent(invitationCode)}/claim`,
    {
      method: "POST",
      body: JSON.stringify({ inviteeUma }),
    }
  );
}

export async function cancelInvitation(invitationCode: string): Promise<any> {
  return request(
    `/invitations/${encodeURIComponent(invitationCode)}/cancel`,
    { method: "POST" }
  );
}

// ─── Sandbox ────────────────────────────────────────────────

export async function sandboxFundAccount(
  accountId: string,
  amount: number
): Promise<any> {
  return request(
    `/sandbox/internal-accounts/${encodeURIComponent(accountId)}/fund`,
    {
      method: "POST",
      body: JSON.stringify({ amount }),
    }
  );
}

export async function sandboxSend(body: {
  quoteId: string;
  currencyCode: string;
  currencyAmount?: number;
}): Promise<any> {
  return request("/sandbox/send", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function sandboxReceive(body: {
  senderUmaAddress: string;
  receiverUmaAddress?: string;
  customerId?: string;
  receivingCurrencyCode: string;
  receivingCurrencyAmount: number;
}): Promise<any> {
  return request("/sandbox/uma/receive", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── Platform Config ────────────────────────────────────────

export async function getConfig(): Promise<any> {
  return request("/config");
}

// ─── Webhooks ───────────────────────────────────────────────

export async function sendTestWebhook(): Promise<any> {
  return request("/webhooks/test", { method: "POST" });
}
